import { scanDepth } from '../../json/depth';
import { significantTokens } from '../../json/lexer';
import {
  ParseErrorKind,
  type RawParseError,
  ScanErrorKind,
  type Token,
  TokenKind,
} from '../../json/tokens';
import type { LintCheck, LintInput } from '../check';
import type { DiagnosticCode } from '../codes';
import type { Params, Span } from '../diagnostic';

/**
 * jsonc's `ParseError` stream, translated into our codes — and, far more importantly,
 * collapsed.
 *
 * A raw `ParseError` is never shown to a user (ADR-0007). It is a *recovery artifact*: the
 * parser reports where it noticed it was lost, not where the mistake is, and it keeps
 * reporting all the way to the end of the construct it abandoned. The measured cascades:
 *
 * ```
 * {a:1}      InvalidSymbol@1+1 | PropertyNameExpected@2+1 | ValueExpected@4+1   3 for 1
 * {'a':'b'}  InvalidSymbol@1+3 | PropertyNameExpected@4+1
 *            | InvalidSymbol@5+3 | ValueExpected@8+1                            4 for 2
 * {"a":01}   CommaExpected@6+1 | PropertyNameExpected@6+1 | ValueExpected@7+1   3 for 1
 * {true:1}   PropertyNameExpected@1+4 | ValueExpected@7+1                       2 for 1
 * {"a":1 2}  CommaExpected@7+1 | PropertyNameExpected@7+1 | ValueExpected@8+1   3 for 1
 * ```
 *
 * Two mechanisms turn those into one diagnostic each.
 *
 * **1. Layer assignment.** jsonc's *lexical* codes (`InvalidSymbol`, the three
 * `UnexpectedEndOf*`, `InvalidUnicode`, `InvalidEscapeCharacter`, `InvalidCharacter`,
 * `InvalidCommentToken`) are dropped outright. They are not suppressed cascades; they are
 * facts `checks/tokens.ts` reports from the same token's `scanError`, anchored better — on
 * the two-character escape instead of the whole string literal, and with the token text as
 * a parameter. Reporting them here too would double every string and number problem in the
 * document. `EndOfFileExpected` is dropped for the same reason: `checks/semantics.ts` owns
 * trailing content, where it can span the whole tail instead of one token.
 *
 * **2. Recovery shadows.** Every *anchor* — a token that is lexically broken, and every
 * token we have already reported a structural problem on — casts a shadow forward to the
 * end of the member it belongs to (the next `,` at the same bracket depth, or the closer of
 * its container). Structural errors inside a shadow are the parser finishing its recovery
 * out loud, and are dropped. A shadow at depth 0 covers only its own token: at top level
 * there is no member to abandon, so a leading BOM must not silence the rest of the file.
 *
 * The mechanism is deliberately one mechanism. Adding the already-emitted anchors is what
 * collapses `{true:1}`, where no token is lexically broken at all and the cascade is purely
 * structural.
 */

const OPENERS: ReadonlySet<TokenKind> = new Set<TokenKind>([
  TokenKind.OpenBrace,
  TokenKind.OpenBracket,
]);

const CLOSERS: ReadonlySet<TokenKind> = new Set<TokenKind>([
  TokenKind.CloseBrace,
  TokenKind.CloseBracket,
]);

/**
 * Raw codes that `checks/tokens.ts` or `checks/semantics.ts` already report, better.
 * Dropping them is a layering decision, not cascade suppression — see the module comment.
 */
const OWNED_ELSEWHERE: ReadonlySet<ParseErrorKind> = new Set<ParseErrorKind>([
  ParseErrorKind.InvalidSymbol,
  ParseErrorKind.InvalidCommentToken,
  ParseErrorKind.UnexpectedEndOfComment,
  ParseErrorKind.UnexpectedEndOfString,
  ParseErrorKind.UnexpectedEndOfNumber,
  ParseErrorKind.InvalidUnicode,
  ParseErrorKind.InvalidEscapeCharacter,
  ParseErrorKind.InvalidCharacter,
  ParseErrorKind.EndOfFileExpected,
]);

/** `,` that is stray or trailing — reported by `checks/tokens.ts`, and an anchor here. */
function isBrokenComma(sig: readonly Token[], index: number): boolean {
  const previous = index > 0 ? sig[index - 1] : undefined;
  const next = sig[index + 1];

  if (previous === undefined) return true;
  if (
    previous.kind === TokenKind.OpenBrace ||
    previous.kind === TokenKind.OpenBracket ||
    previous.kind === TokenKind.Comma ||
    previous.kind === TokenKind.Colon
  ) {
    return true;
  }
  return next !== undefined && CLOSERS.has(next.kind);
}

/**
 * A token `checks/tokens.ts` will report on.
 *
 * Kept in step with that file by `structure.test.ts`, which asserts over a corpus that
 * every token marked here really does carry a diagnostic from the token check. The two
 * cannot share a helper without a fourth file under `checks/`, so they share a test.
 */
function isSuspect(sig: readonly Token[], index: number, source: string): boolean {
  const token = sig[index];
  if (token === undefined) return false;
  if (token.kind === TokenKind.EOF) return false;
  if (token.kind === TokenKind.Unknown) return true;
  if (token.scanError !== ScanErrorKind.None) return true;
  if (token.kind === TokenKind.Comma) return isBrokenComma(sig, index);

  if (token.kind === TokenKind.Number) {
    // `01`, `0xFF` and `1.2.3` all arrive as two or three adjacent tokens.
    const previous = index > 0 ? sig[index - 1] : undefined;
    const next = sig[index + 1];
    if (
      previous !== undefined &&
      previous.kind === TokenKind.Number &&
      previous.to === token.from
    ) {
      return true;
    }
    if (next !== undefined && next.from === token.to) {
      if (next.kind === TokenKind.Number) return true;
      if (
        next.kind === TokenKind.Unknown &&
        /^[0-9A-Za-z.]/.test(source.slice(next.from, next.to))
      ) {
        return true;
      }
    }
  }

  return false;
}

/** Bracket depth *before* each significant token. */
function depthsOf(sig: readonly Token[]): number[] {
  const depths: number[] = [];
  let depth = 0;

  for (const token of sig) {
    depths.push(depth);
    if (OPENERS.has(token.kind)) depth += 1;
    else if (CLOSERS.has(token.kind) && depth > 0) depth -= 1;
  }

  return depths;
}

/**
 * For every index, the offset at which a forward scan starting there would stop: the first
 * `,` or closer at its own bracket depth, or the end of the document.
 *
 * WHY IT IS A TABLE. `shadowFrom` used to run that scan itself, once per anchor, and the
 * anchors on a broken document are proportional to its length — so on input with no commas
 * to stop at, every scan ran to EOF and the pair was quadratic. `["` + `a⏎` × 16 000 + `"]`
 * is 32 KB of exactly that shape (a multi-line string, i.e. a log paste) and cost 849 ms of
 * `lint`, 1 328 ms of it measured inside this one function. One backward pass answers every
 * anchor in O(1), and the same 32 KB now lints in 22 ms.
 *
 * Both passes are plain bracket matching that ignores brace-vs-bracket kind, because the
 * scan they replace did too: `relative` counted openers and closers without caring which.
 */
function memberEndsOf(sig: readonly Token[]): number[] {
  const n = sig.length;
  /** For each opener, the index of the closer that balances it; EOF when it has none. */
  const closerOf = new Int32Array(n).fill(-1);
  const open: number[] = [];

  for (let i = 0; i < n; i += 1) {
    const token = sig[i];
    if (token === undefined) continue;
    if (OPENERS.has(token.kind)) open.push(i);
    else if (CLOSERS.has(token.kind)) {
      const at = open.pop();
      if (at !== undefined) closerOf[at] = i;
    }
  }

  const ends: number[] = new Array<number>(n).fill(0);

  for (let i = n - 1; i >= 0; i -= 1) {
    const token = sig[i];
    if (token === undefined) continue;

    if (
      token.kind === TokenKind.EOF ||
      token.kind === TokenKind.Comma ||
      CLOSERS.has(token.kind)
    ) {
      ends[i] = token.to;
      continue;
    }

    if (OPENERS.has(token.kind)) {
      const close = closerOf[i];
      const after = close === undefined || close < 0 ? n : close + 1;
      ends[i] = after < n ? (ends[after] ?? token.to) : (sig[n - 1]?.to ?? token.to);
      continue;
    }

    ends[i] = i + 1 < n ? (ends[i + 1] ?? token.to) : token.to;
  }

  return ends;
}

/**
 * How far the parser's recovery from a problem at `index` can reasonably reach: to the end
 * of the member that token belongs to.
 */
function shadowFrom(
  sig: readonly Token[],
  depths: readonly number[],
  memberEnds: readonly number[],
  index: number,
): Span {
  const anchor = sig[index];
  if (anchor === undefined) return { from: 0, to: 0 };

  // Top level has no member to abandon, so top-level junk shadows nothing but itself.
  if ((depths[index] ?? 0) === 0) return { from: anchor.from, to: anchor.to };

  const to = memberEnds[index + 1];
  return { from: anchor.from, to: to ?? anchor.to };
}

/**
 * Index of the significant token an error at `offset` is talking about.
 *
 * Binary search, not a scan. Token spans are disjoint and strictly increasing, so the answer
 * is "the first token that does not end before `offset`" — which is exactly what the linear
 * version computed, one token at a time, once per raw parse error. A 600 KiB document of
 * `1,,1,,1,,…` produces 200 000 raw errors against 400 000 significant tokens, and the two
 * multiplied: measured at 19.5 seconds inside this check while the lexer and the parser
 * together took 120 ms. See the note on `ShadowSet` below; this is the other half of the
 * same fix.
 */
function tokenIndexAt(sig: readonly Token[], offset: number): number {
  let low = 0;
  let high = sig.length;

  while (low < high) {
    const mid = (low + high) >>> 1;
    const token = sig[mid];
    if (token === undefined) break;
    if (token.to <= offset) low = mid + 1;
    else high = mid;
  }

  return low < sig.length ? low : Math.max(sig.length - 1, 0);
}

/**
 * The set of regions the parser's recovery is allowed to shadow, as disjoint merged
 * intervals with a binary-search membership test.
 *
 * WHY THIS IS NOT AN ARRAY AND A `.some()`. It was, and that was a live quadratic — the
 * second half of the 19.5 seconds above. The membership test runs once per raw parse error,
 * the set grows by one for every diagnostic this check emits, and on a document that is
 * mostly mistakes both numbers are in the hundreds of thousands. `lint()` on 600 KiB of
 * `1,,` took 23 seconds end to end, of which 19.5 were here; on a 2 MiB document it is the
 * hung tab the size guard exists to prevent, arriving through the front door instead.
 *
 * Merging is behaviour-preserving, not an approximation: `[a,b) ∪ [c,d)` is collapsed only
 * when `c <= b`, and a point is in the union exactly when it is in the collapsed interval.
 * It is also what makes this fast on the input that hurt — overlapping shadows are the
 * pathological case, and they merge down to a handful of intervals.
 */
class ShadowSet {
  private readonly from: number[] = [];
  private readonly to: number[] = [];

  add(span: Span): void {
    if (span.to <= span.from) return;

    // Fast path: shadows arrive left to right (the token loop walks forwards, and so do the
    // parser's errors), so the overwhelmingly common insert extends or follows the last one.
    const last = this.from.length - 1;
    const lastTo = this.to[last];
    const lastFrom = this.from[last];
    if (last >= 0 && lastFrom !== undefined && lastTo !== undefined && span.from >= lastFrom) {
      if (span.from <= lastTo) this.to[last] = Math.max(lastTo, span.to);
      else {
        this.from.push(span.from);
        this.to.push(span.to);
      }
      return;
    }

    const at = this.lowerBound(span.from);
    this.from.splice(at, 0, span.from);
    this.to.splice(at, 0, span.to);
    this.mergeAround(at);
  }

  covers(offset: number): boolean {
    // The last interval starting at or before `offset`; intervals are disjoint, so it is the
    // only one that can contain it.
    const at = this.lowerBound(offset + 1) - 1;
    if (at < 0) return false;
    const to = this.to[at];
    return to !== undefined && offset < to;
  }

  /** First index whose `from` is >= `value`. */
  private lowerBound(value: number): number {
    let low = 0;
    let high = this.from.length;

    while (low < high) {
      const mid = (low + high) >>> 1;
      const from = this.from[mid];
      if (from === undefined) break;
      if (from < value) low = mid + 1;
      else high = mid;
    }

    return low;
  }

  private mergeAround(index: number): void {
    let at = index;
    const previousTo = this.to[at - 1];
    const currentFrom = this.from[at];
    if (
      at > 0 &&
      previousTo !== undefined &&
      currentFrom !== undefined &&
      currentFrom <= previousTo
    ) {
      this.to[at - 1] = Math.max(previousTo, this.to[at] ?? previousTo);
      this.from.splice(at, 1);
      this.to.splice(at, 1);
      at -= 1;
    }

    for (;;) {
      const to = this.to[at];
      const nextFrom = this.from[at + 1];
      if (to === undefined || nextFrom === undefined || nextFrom > to) break;
      this.to[at] = Math.max(to, this.to[at + 1] ?? to);
      this.from.splice(at + 1, 1);
      this.to.splice(at + 1, 1);
    }
  }
}

/** The innermost container still open at `offset`, as its opening token. */
function openContainerAt(
  sig: readonly Token[],
  offset: number,
  kind: TokenKind,
): Token | undefined {
  const stack: Token[] = [];

  for (const token of sig) {
    if (token.from >= offset) break;
    if (OPENERS.has(token.kind)) stack.push(token);
    else if (CLOSERS.has(token.kind)) stack.pop();
  }

  for (let i = stack.length - 1; i >= 0; i -= 1) {
    const opener = stack[i];
    if (opener !== undefined && opener.kind === kind) return opener;
  }
  return undefined;
}

function innermostContainerKind(
  sig: readonly Token[],
  offset: number,
): 'object' | 'array' | undefined {
  const stack: TokenKind[] = [];

  for (const token of sig) {
    if (token.from >= offset) break;
    if (OPENERS.has(token.kind)) stack.push(token.kind);
    else if (CLOSERS.has(token.kind)) stack.pop();
  }

  const top = stack[stack.length - 1];
  if (top === TokenKind.OpenBrace) return 'object';
  if (top === TokenKind.OpenBracket) return 'array';
  return undefined;
}

interface Translation {
  readonly code: DiagnosticCode;
  readonly params?: Params;
}

function translate(
  input: LintInput,
  sig: readonly Token[],
  error: RawParseError,
): Translation | undefined {
  const { source } = input;

  switch (error.kind) {
    case ParseErrorKind.InvalidNumberFormat:
      return {
        code: 'number/invalid-literal',
        params: { text: source.slice(error.offset, error.offset + error.length) },
      };

    case ParseErrorKind.PropertyNameExpected:
      return { code: 'syntax/property-name-expected' };

    case ParseErrorKind.ValueExpected:
      // A zero-width "a value goes here" at the very end of the file is not a missing
      // value in the middle of a document — it is a document that was cut short.
      return error.length === 0 && error.offset === source.length
        ? { code: 'syntax/unexpected-eof' }
        : { code: 'syntax/value-expected' };

    case ParseErrorKind.ColonExpected: {
      const index = tokenIndexAt(sig, error.offset);
      const key = index > 0 ? sig[index - 1] : undefined;
      return key === undefined
        ? { code: 'syntax/colon-expected' }
        : { code: 'syntax/colon-expected', params: { key: source.slice(key.from, key.to) } };
    }

    case ParseErrorKind.CommaExpected: {
      const kind = innermostContainerKind(sig, error.offset);
      return kind === undefined
        ? { code: 'syntax/comma-expected' }
        : { code: 'syntax/comma-expected', params: { kind } };
    }

    case ParseErrorKind.CloseBraceExpected:
    case ParseErrorKind.CloseBracketExpected: {
      const isBrace = error.kind === ParseErrorKind.CloseBraceExpected;
      const code: DiagnosticCode = isBrace
        ? 'syntax/close-brace-expected'
        : 'syntax/close-bracket-expected';
      const opener = openContainerAt(
        sig,
        error.offset,
        isBrace ? TokenKind.OpenBrace : TokenKind.OpenBracket,
      );
      return opener === undefined
        ? { code }
        : { code, params: { line: input.lines.positionAt(opener.from).line } };
    }

    default:
      return undefined;
  }
}

export const structureCheck: LintCheck = {
  id: 'structure',

  run(input, emit) {
    const sig = significantTokens(input.tokens);

    // The EOF sentinel is always present, so "one token" means "nothing but trivia".
    if (sig.length <= 1) {
      emit({ code: 'syntax/empty-document', span: { from: 0, to: 0 } });
      return;
    }

    if (input.tree === undefined && input.rawErrors.length === 0) {
      // `json/parse.ts` documents exactly three ways to get no tree. Empty content was
      // handled above, so this is either the depth guard or the `RangeError` backstop, and
      // re-scanning the (iterative) token stream tells the two apart and gives the anchor.
      const depth = scanDepth(input.tokens, input.options.maxDepth);

      if (depth.exceededAt !== -1) {
        emit({
          code: 'semantic/deep-nesting',
          span: { from: depth.exceededAt, to: depth.exceededAt + 1 },
          params: { depth: depth.maxDepth, limit: input.options.maxDepth },
        });

        // …AND an abort, which is the half that was missing. `semantic/deep-nesting` is a
        // `warning` on purpose — a 600-level document is still JSON — but the parse was
        // SKIPPED, so nothing below this line ever looked for a syntax error. Emitting only
        // the warning made `report.ok` true for a document nobody validated: measured on
        // JSONTestSuite's `n_structure_100000_opening_arrays.json`, 100 000 unclosed `[`
        // with not one closer, which came back green and which `formatAnalyzed` would then
        // cheerfully pretty-print. Silence from a check that did not run is not evidence.
        emit({
          code: 'internal/analysis-aborted',
          span: { from: 0, to: 0 },
          params: { reason: 'depth-exceeded' },
        });
      } else {
        emit({
          code: 'internal/analysis-aborted',
          span: { from: 0, to: 0 },
          params: { reason: 'no-tree' },
        });
      }
      return;
    }

    const depths = depthsOf(sig);
    const memberEnds = memberEndsOf(sig);
    const shadows = new ShadowSet();

    for (let i = 0; i < sig.length; i += 1) {
      if (isSuspect(sig, i, input.source)) shadows.add(shadowFrom(sig, depths, memberEnds, i));
    }

    for (const error of input.rawErrors) {
      if (OWNED_ELSEWHERE.has(error.kind)) continue;
      if (shadows.covers(error.offset)) continue;

      const translation = translate(input, sig, error);
      if (translation === undefined) continue;

      const span: Span = { from: error.offset, to: error.offset + error.length };
      emit(
        translation.params === undefined
          ? { code: translation.code, span }
          : { code: translation.code, span, params: translation.params },
      );

      // What we just said is itself a recovery anchor: everything jsonc goes on to report
      // about the rest of this member is the same mistake, seen again.
      shadows.add(shadowFrom(sig, depths, memberEnds, tokenIndexAt(sig, error.offset)));
    }
  },
};
